import * as React from 'react';
import * as api from '../services/api-client';
import { NetworkError, ApiErrorResponse } from '../services/api-client';

/**
 * Credential state management for the Admin Console.
 * System_Admin only: store, view, and rotate Cloudflare credentials.
 * Implements optimistic updates with revert on failure.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Credential {
  readonly credentialId: string;
  readonly description: string;
  readonly domains: readonly string[];
  readonly status: 'active' | 'rotating';
  readonly validatedAt: string;
  readonly createdBy: string;
}

export interface StoreCredentialInput {
  readonly domains: readonly string[];
  readonly apiToken: string;
  readonly description: string;
}

export interface RotateCredentialInput {
  readonly credentialId: string;
  readonly newApiToken: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

export interface CredentialState {
  readonly credentials: readonly Credential[];
  readonly isLoading: boolean;
  readonly error: string | null;
}

export const initialCredentialState: CredentialState = {
  credentials: [],
  isLoading: false,
  error: null,
};

// ─── Actions ─────────────────────────────────────────────────────────────────

type CredentialAction =
  | { readonly type: 'CREDENTIALS_LOADING' }
  | { readonly type: 'CREDENTIALS_LOADED'; readonly payload: readonly Credential[] }
  | { readonly type: 'CREDENTIALS_ERROR'; readonly payload: string }
  | { readonly type: 'CREDENTIAL_STORE_OPTIMISTIC'; readonly payload: Credential }
  | { readonly type: 'CREDENTIAL_STORE_CONFIRMED' }
  | { readonly type: 'CREDENTIAL_STORE_REVERT'; readonly payload: { readonly previousState: readonly Credential[] } }
  | { readonly type: 'CREDENTIAL_ROTATE_OPTIMISTIC'; readonly payload: { readonly credentialId: string } }
  | { readonly type: 'CREDENTIAL_ROTATE_CONFIRMED'; readonly payload: { readonly credentialId: string } }
  | { readonly type: 'CREDENTIAL_ROTATE_REVERT'; readonly payload: { readonly previousState: readonly Credential[] } };

// ─── Reducer ─────────────────────────────────────────────────────────────────

export function credentialReducer(state: CredentialState, action: CredentialAction): CredentialState {
  switch (action.type) {
    case 'CREDENTIALS_LOADING':
      return { ...state, isLoading: true, error: null };
    case 'CREDENTIALS_LOADED':
      return { credentials: action.payload, isLoading: false, error: null };
    case 'CREDENTIALS_ERROR':
      return { ...state, isLoading: false, error: action.payload };
    case 'CREDENTIAL_STORE_OPTIMISTIC':
      return { ...state, credentials: [...state.credentials, action.payload], error: null };
    case 'CREDENTIAL_STORE_CONFIRMED':
      return state;
    case 'CREDENTIAL_STORE_REVERT':
      return { ...state, credentials: action.payload.previousState, error: 'Failed to store credential. Reverted.' };
    case 'CREDENTIAL_ROTATE_OPTIMISTIC':
      return {
        ...state,
        credentials: state.credentials.map((c) =>
          c.credentialId === action.payload.credentialId ? { ...c, status: 'rotating' as const } : c,
        ),
        error: null,
      };
    case 'CREDENTIAL_ROTATE_CONFIRMED':
      return {
        ...state,
        credentials: state.credentials.map((c) =>
          c.credentialId === action.payload.credentialId ? { ...c, status: 'active' as const } : c,
        ),
      };
    case 'CREDENTIAL_ROTATE_REVERT':
      return { ...state, credentials: action.payload.previousState, error: 'Credential rotation failed. Reverted.' };
    default:
      return state;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

interface CredentialContextValue {
  readonly state: CredentialState;
  readonly loadCredentials: () => Promise<void>;
  readonly storeCredential: (input: StoreCredentialInput) => Promise<void>;
  readonly rotateCredential: (input: RotateCredentialInput) => Promise<void>;
  readonly isConnecting: boolean;
}

const CredentialContext = React.createContext<CredentialContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

interface CredentialProviderProps {
  readonly children: React.ReactNode;
}

/**
 * CredentialProvider manages Cloudflare credential state with optimistic updates.
 * System_Admin only — authorization enforced at the route/page level.
 */
export function CredentialProvider({ children }: CredentialProviderProps): React.JSX.Element {
  const [state, dispatch] = React.useReducer(credentialReducer, initialCredentialState);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const stateRef = React.useRef(state.credentials);
  stateRef.current = state.credentials;

  const loadCredentials = React.useCallback(async (): Promise<void> => {
    dispatch({ type: 'CREDENTIALS_LOADING' });

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 2000, 3000];

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const resp = await api.get<{ credentials?: readonly Credential[] }>('/admin/credentials');
        const data = resp.credentials ?? [];
        dispatch({ type: 'CREDENTIALS_LOADED', payload: data });
        setIsConnecting(false);
        return;
      } catch (err) {
        const isRetryable =
          err instanceof NetworkError ||
          (err instanceof ApiErrorResponse && err.status >= 500);

        if (isRetryable && attempt < MAX_RETRIES) {
          setIsConnecting(true);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS[attempt]));
          continue;
        }

        setIsConnecting(false);
        dispatch({
          type: 'CREDENTIALS_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to load credentials',
        });
        return;
      }
    }
  }, []);

  const storeCredential = React.useCallback(async (input: StoreCredentialInput): Promise<void> => {
    const previousState = stateRef.current;
    const optimistic: Credential = {
      credentialId: crypto.randomUUID(),
      description: input.description,
      domains: input.domains,
      status: 'active',
      validatedAt: new Date().toISOString(),
      createdBy: 'current-user',
    };

    dispatch({ type: 'CREDENTIAL_STORE_OPTIMISTIC', payload: optimistic });
    try {
      await api.post('/admin/credentials', input);
      dispatch({ type: 'CREDENTIAL_STORE_CONFIRMED' });
      // Reload to get server-assigned IDs
      await loadCredentials();
    } catch {
      dispatch({ type: 'CREDENTIAL_STORE_REVERT', payload: { previousState } });
    }
  }, [loadCredentials]);

  const rotateCredential = React.useCallback(async (input: RotateCredentialInput): Promise<void> => {
    const previousState = stateRef.current;

    dispatch({ type: 'CREDENTIAL_ROTATE_OPTIMISTIC', payload: { credentialId: input.credentialId } });
    try {
      await api.post('/admin/credentials/rotate', input);
      dispatch({ type: 'CREDENTIAL_ROTATE_CONFIRMED', payload: { credentialId: input.credentialId } });
    } catch {
      dispatch({ type: 'CREDENTIAL_ROTATE_REVERT', payload: { previousState } });
    }
  }, []);

  const contextValue = React.useMemo(
    (): CredentialContextValue => ({
      state,
      loadCredentials,
      storeCredential,
      rotateCredential,
      isConnecting,
    }),
    [state, loadCredentials, storeCredential, rotateCredential, isConnecting],
  );

  return React.createElement(CredentialContext.Provider, { value: contextValue }, children);
}

// ─── Consumer Hook ───────────────────────────────────────────────────────────

/**
 * Hook to access credential state and actions.
 * Must be used within a CredentialProvider.
 */
export function useCredentials(): CredentialContextValue {
  const context = React.useContext(CredentialContext);
  if (!context) {
    throw new Error('useCredentials must be used within a CredentialProvider');
  }
  return context;
}
